import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { LocalStorageService } from 'src/app/local-storage.service';
import { environment } from 'src/environments/environment';
import { APPMODE } from 'src/environments/appmode.enum';
import { WINDOW } from './window-providers';
import { ORIGIN_TYPE } from './store/store-checkout/checkout.service';
import { isTemplateBottomBarApp } from './store/utils/WidgetPageUtils';
import { CookieService } from 'ngx-cookie-service';
import { FormattedTimePipe } from '../shared/formatted-time.pipe';
import {
  EventDetailsDTO,
  EventStatistics,
  SubEventDTO,
} from '../stores/store-events-bookings/store-events-bookings';
import { Capacitor } from '@capacitor/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl } from '@angular/forms';
import { DateTime } from 'luxon';

/**
 * Whether this is the PayDroid build of GoAdmin: the APK made for PAX payment
 * terminals (card #9881).
 *
 * A PAX terminal has no Google Mobile Services, no Play Store and few other
 * apps, so the parts of GoAdmin that lean on those — Firebase push, Google
 * sign-in, Facebook sign-in (the Facebook app or a Custom Tabs browser) —
 * cannot work there, and the app has to know it is on one. The build says so
 * through `environment.paydroid`, which the Capacitor build
 * (`capacitor-build-utils.js`) writes into the GoAdmin environment when
 * `PAYDROID_VARIANT` is set; the Google Play GoAdmin, iOS, every other app and
 * the web keep the base `false`. The mode is checked as well, so the flag can
 * never switch on GoAdmin-only behaviour in another app.
 *
 * A plain function as well as `HelperService.isPayDroidBuild`, for code that
 * has no injector to ask — `PrintersEffects`, whose integration spec provides
 * a stand-in `HelperService`. Both are the same check.
 */
export function isPayDroidBuild(): boolean {
  return environment.mode === APPMODE.Admin && environment.paydroid === true;
}

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  private formattedTimePipe = new FormattedTimePipe();
  constructor(
    // should be standalone dependencies to avoid circular dependencies issue
    private storageService: LocalStorageService,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: any,
    private cookieService: CookieService,
  ) {}

  deviceIdentifierCheck = { flag: false, orderId: '' };

  getBackgroundOptionImage(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    // Escape CSS string delimiters without changing existing URL encoding or
    // signed query parameters. Return a string so style bindings remain stable.
    const escaped = url.replace(
      /["\\\n\r\f]/g,
      (character) => `\\${character.charCodeAt(0).toString(16)} `,
    );
    return `url("${escaped}")`;
  }

  getPublishedSubEvents(
    eventDetails: EventDetailsDTO | null | undefined,
  ): SubEventDTO[] {
    const subEvents = Array.isArray(eventDetails?.subEvents)
      ? eventDetails.subEvents
      : [];

    return subEvents.filter((subEvent) => subEvent.status === 'PUBLISHED');
  }

  formatText(content: string): string {
    const regex = new RegExp(
      // protocol part(optional)
      '(([\\w]+:)?//)?' +
        // email address local part(optional)
        '(' +
        '([\\d\\w]|%[a-f\\d]{2,2})+' +
        '(:([\\d\\w]|%[a-f\\d]{2,2})+)?' +
        '@' +
        ')?' +
        // domain part
        '([\\d\\w][-\\d\\w]{0,253}[\\d\\w]\\.)+' +
        '[\\w]{2,63}' +
        // port number part(optional)
        '(:[\\d]+)?' +
        // path part(optional)
        '(/([-+_~.\\d\\w]|%[a-f\\d]{2,2})*)*' +
        // hash and query param part(optional)
        '(' +
        // allow query param after hash(optional) (extral requirement)
        '(#(/?[-+_~.\\d\\w]|%[a-f\\d]{2,2})*)(\\?(&?([-+_~.\\d\\w]|%[a-f\\d]{2,2})[=]?)*)' +
        '|' +
        // query param part(optional)
        '(\\?(&?([-+_~.\\d\\w]|%[a-f\\d]{2,2})[=]?)*)?' +
        // hash part(optional)
        '(#(/?[-+_~.\\d\\w]|%[a-f\\d]{2,2})*)?' +
        ')',
      'ig',
    );
    let result = content.replace(regex, (url) => {
      return '<a href="' + url + '">' + url + '</a>';
    });
    const boldStrings = result.split('**');
    result = boldStrings
      .map((str, index) => {
        if (index % 2 && index !== boldStrings.length - 1) {
          return '<b>' + str + '</b>';
        }
        return str;
      })
      .join('');
    const italicStrings = result.split('__');
    result = italicStrings
      .map((str, index) => {
        if (index % 2 && index !== boldStrings.length - 1) {
          return '<i>' + str + '</i>';
        }
        return str;
      })
      .join('');
    return result;
  }

  getExcerpt(content: string, numOfChars: number) {
    if (!content || content.length === 0) {
      return {
        excerpt: '',
        reminder: '',
      };
    }
    if (content.length < numOfChars) {
      return {
        excerpt: this.formatText(content.replace(/(\r\n|\n|\r)/gm, '<br>')),
        reminder: '',
      };
    }
    let excerptCont = content.substr(0, numOfChars);

    // re-trim if we are in the middle of a word
    excerptCont = excerptCont.substr(
      0,
      Math.min(
        excerptCont.length,
        excerptCont.lastIndexOf(' ')
          ? excerptCont.lastIndexOf(' ')
          : excerptCont.length,
      ),
    );
    return {
      excerpt: this.formatText(excerptCont.replace(/(\r\n|\n|\r)/gm, '<br>')),
      reminder: this.formatText(
        content.substring(excerptCont.length).replace(/(\r\n|\n|\r)/gm, '<br>'),
      ),
    };
  }

  checkLocationInputValidity(value, min = 0, max = 0) {
    if (min !== 0 && max !== 0) {
      const regex = new RegExp('^([a-z0-9-_ ]){' + min + ',' + max + '}$', 'i');
      return regex.test(value);
    }
    return /^([a-z0-9-_ ]){1,10}$/i.test(value);
  }

  // not in use anymore
  // it will trunn a string into acceptable slug
  slugify(val: string) {
    const a =
      'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
    const b =
      'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return val
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, ''); // Trim - from end of text
  }

  /**
   * check if the value passed is valid language two or three letter iso
   * @param val string, language two or three letter iso
   */
  checkValidLangIso(val: string) {
    const regex = new RegExp('^[a-z]{2,3}$');
    return regex.test(val);
  }

  scrollCalc(el: ElementRef = null) {
    if (el != null) {
      if (
        el.nativeElement.offsetHeight + el.nativeElement.scrollTop >=
          el.nativeElement.scrollHeight ||
        (!el.nativeElement.scrollTop &&
          el.nativeElement.offsetHeight === el.nativeElement.scrollHeight)
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      const h =
        this.window.innerHeight ||
        this.document.documentElement.clientHeight ||
        this.document.body.clientHeight;
      if (this.window.scrollY + h >= this.document.body.scrollHeight) {
        return false;
      } else {
        return true;
      }
    }
  }
  scrollShowElement(el: ElementRef) {
    if (!el) {
      return false;
    }
    const elPos = el.nativeElement.offsetTop + el.nativeElement.offsetHeight;
    if (this.window.scrollY > elPos) {
      return true;
    } else {
      return false;
    }
  }
  scrollPage(el: ElementRef = null) {
    const subject: Subject<any> = new Subject<any>();
    this.doScrolling(el, 300, 50, false, 'down', subject);
  }

  scrollTo(offsetTop: number, scrollSpeed = 700, el: ElementRef = null) {
    const subject: Subject<any> = new Subject<any>();
    this.doScrolling(el, scrollSpeed, offsetTop, true, 'down', subject);
  }

  scrollElementIntoContainer({
    container,
    element,
    alignToTop = false,
    behavior = 'auto',
  }: {
    container: HTMLElement | null | undefined;
    element: HTMLElement | null | undefined;
    alignToTop?: boolean;
    behavior?: ScrollBehavior;
  }): boolean {
    if (
      !(container instanceof HTMLElement) ||
      !(element instanceof HTMLElement)
    ) {
      return false;
    }

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const relativeTop =
      elementRect.top - containerRect.top + container.scrollTop;
    const relativeBottom = relativeTop + elementRect.height;
    const containerStyles = this.window.getComputedStyle(container);
    const elementStyles = this.window.getComputedStyle(element);
    const safeAreaBottom = Number.parseFloat(
      this.document.documentElement.style.getPropertyValue(
        '--safe-area-inset-bottom',
      ) ||
        this.window
          .getComputedStyle(this.document.documentElement)
          .getPropertyValue('--safe-area-inset-bottom'),
    );
    const topPadding =
      (Number.parseFloat(containerStyles.paddingTop) || 0) +
      (Number.parseFloat(elementStyles.marginTop) || 0);
    const bottomPadding =
      (Number.parseFloat(containerStyles.paddingBottom) || 0) +
      (Number.parseFloat(elementStyles.marginBottom) || 0) +
      (Number.isFinite(safeAreaBottom) ? safeAreaBottom : 0);
    const visibleTop = container.scrollTop + topPadding;
    const visibleBottom =
      container.scrollTop + container.clientHeight - bottomPadding;

    let nextScrollTop = container.scrollTop;
    if (alignToTop) {
      nextScrollTop = relativeTop - topPadding;
    } else if (relativeTop < visibleTop) {
      nextScrollTop = relativeTop - topPadding;
    } else if (relativeBottom > visibleBottom) {
      nextScrollTop = relativeBottom - container.clientHeight + bottomPadding;
    }

    if (nextScrollTop !== container.scrollTop) {
      const normalizedScrollTop = Math.max(0, nextScrollTop);
      container.scrollTop = normalizedScrollTop;
      container.scrollTo({
        top: normalizedScrollTop,
        behavior,
      });
    }

    return true;
  }

  doScrolling(
    el,
    duration,
    scrollBy,
    fromTop,
    direction,
    subject: Subject<any>,
  ) {
    const w = this.window;
    let startingY = 0;
    let currentY = 0;
    if (el != null) {
      startingY = currentY = el.nativeElement.scrollTop;
    } else {
      startingY = currentY = w.scrollY;
    }
    if (fromTop) {
      scrollBy = scrollBy - startingY;
      if (scrollBy === currentY) {
        return;
      }
    }
    let start;

    w.requestAnimationFrame(function step(timestamp) {
      start = !start ? timestamp : start;
      const time = timestamp - start;
      const percent = Math.min(time / duration, 1);
      if (el != null) {
        direction === 'down'
          ? (el.nativeElement.scrollTop =
              startingY + Math.floor(scrollBy * percent))
          : (el.nativeElement.scrollTop =
              startingY - Math.floor(scrollBy * percent));
      } else {
        direction === 'down'
          ? w.scroll(0, startingY + Math.floor(scrollBy * percent))
          : w.scroll(0, startingY - Math.floor(scrollBy * percent));
      }

      if (time < duration) {
        w.requestAnimationFrame(step);
        subject.next({});
      } else {
        subject.complete();
      }
    });
  }

  focusTextArea(element) {
    if (element.children[0].nodeName === 'TEXTAREA') {
      element.children[0].focus();
      if (this.isAndroid()) {
        setTimeout(() => {
          element.children[0].scrollIntoView();
        }, 300);
      }
    }
  }

  isAndroid() {
    const userAgent = navigator.userAgent || navigator.vendor;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return false;
    }
    if (/android/i.test(userAgent)) {
      return true;
    }
    return false;
  }

  isIOS() {
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.indexOf('ipad') > -1 ||
      ua.indexOf('iphone') > -1 ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  isMobileApp() {
    return environment.mode !== APPMODE.Web;
  }

  isIosApp() {
    return (
      Capacitor.getPlatform() === 'ios' && environment.mode !== APPMODE.Web
    );
  }

  async isSha256(message: string) {
    // encode as UTF-8
    const msgBuffer = new TextEncoder().encode(message);

    // hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

    // convert ArrayBuffer to Array
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    // convert bytes to hex string
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return hashHex;
  }

  calcPaddingBottom(
    screenHeight: number,
    isIOSApp: boolean,
    topCategoryContainerMarginBottom: number,
    isGoApp: boolean,
  ): any {
    let paddingBottom;
    if (screenHeight <= 568) {
      paddingBottom = '65vh';
    } else if (screenHeight <= 667) {
      paddingBottom = '60vh';
    } else if (screenHeight <= 736) {
      paddingBottom = '55vh';
    } else {
      paddingBottom = '50vh';
    }

    let styles = {};
    if (isIOSApp) {
      styles = {
        ...styles,
        'max-height': '80vh',
        'overflow-y': 'auto',
        'padding-bottom': paddingBottom, // Use the dynamically set padding
      };
    }
    if (isGoApp) {
      styles['margin-bottom.px'] = topCategoryContainerMarginBottom;
    }
    return styles;
  }

  calcInnerHeight() {
    const lgWidth = 992;
    const headerHeightWithoutSidebar = 100;
    const headerHeightWithSidebar = 120;
    if (this.window.innerWidth < lgWidth) {
      return this.window.innerHeight - headerHeightWithoutSidebar;
    }
    return this.window.innerHeight - headerHeightWithSidebar;
  }

  getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
  }

  generateDeviceID() {
    const pattern = [8, 4, 4, 4, 12];
    const chars = 'abcdef';
    return pattern
      .map((len) => {
        return Array(len)
          .fill(0)
          .map(() => {
            const i = this.getRandomInt(16);
            return i < 10 ? i.toString() : chars[i - 10];
          })
          .join('');
      })
      .join('-');
  }

  getDeviceID() {
    let deviceID = this.storageService.getSavedState('deviceID');
    if (!deviceID) {
      deviceID = this.generateDeviceID();
      this.storageService.setSavedState(deviceID, 'deviceID');
    }
    return deviceID;
  }
  getDomainUrl() {
    let domainUrl = window.location.host;
    if (!window.location.host.startsWith(environment.envDomain)) {
      // domainUrl = domainUrl.split('.').slice(1).join('.');
      domainUrl = environment.envDomain;
    }
    return domainUrl;
  }

  /**
   * Process opening time schedule in STORE timezone
   * CRITICAL: Opening hours must ALWAYS be displayed in the store's timezone, not the browser's
   * This ensures a store in New York shows its hours correctly to customers anywhere in the world
   */
  processOpenTimeSchedule(schedule, timezoneStore, timeFormat, locale) {
    const availabitity = schedule.availabilities;
    const result = {
      MON: {
        title: 'public.global.days.mon',
        items: [],
      },
      TUE: {
        title: 'public.global.days.tue',
        items: [],
      },
      WED: {
        title: 'public.global.days.wed',
        items: [],
      },
      THU: {
        title: 'public.global.days.thu',
        items: [],
      },
      FRI: {
        title: 'public.global.days.fri',
        items: [],
      },
      SAT: {
        title: 'public.global.days.sat',
        items: [],
      },
      SUN: {
        title: 'public.global.days.sun',
        items: [],
      },
    };

    const storeTimezone = timezoneStore || 'UTC';

    availabitity.map((item) => {
      // Parse times as UTC first (backend sends UTC times)
      // Times are in format "HH:mm:ss" or "HH:mm"
      const startTimeStr = item.startTime.substring(0, 5); // Get HH:mm
      const endTimeStr = item.endTime.substring(0, 5); // Get HH:mm

      if (item.daysOfWeek) {
        for (const dayOfWeek of item.daysOfWeek) {
          result[dayOfWeek].items.push({
            startTime: startTimeStr,
            endTime: endTimeStr,
            originalStartTime: item.startTime,
            originalEndTime: item.endTime,
          });
        }
      } else if (!item.date) {
        for (const day of Object.keys(result)) {
          result[day].items.push({
            startTime: startTimeStr,
            endTime: endTimeStr,
            originalStartTime: item.startTime,
            originalEndTime: item.endTime,
          });
        }
      }
    });

    const resultArray = Object.values(result);

    for (let i = 0; i < resultArray.length; i++) {
      const nextI = (i + 1) % 7;

      resultArray[i].items.sort((a, b) =>
        a.startTime.localeCompare(b.startTime),
      );

      resultArray[i].items.forEach((item) => {
        // Merge time slots for consecutive days (e.g., 22:00-02:00 shows as "10pm - 2am")
        // This handles venues open past midnight
        if (['23:59', '00:00'].includes(item.endTime)) {
          const nextDayItem = resultArray[nextI].items.find(
            (nextItem) =>
              ['00:00', '00:01'].includes(nextItem.startTime) &&
              nextItem.endTime <= '06:00' &&
              nextItem.endTime !== '00:00',
          );
          if (nextDayItem) {
            // Merge the end time from next day into current day
            // So "Mon 22:00-00:00" + "Tue 00:00-02:00" becomes "Mon 22:00-02:00"
            item.endTime = nextDayItem.endTime;
            item.originalEndTime = nextDayItem.originalEndTime;
            item.spansMidnight = true;
            resultArray[nextI].items.splice(
              resultArray[nextI].items.indexOf(nextDayItem),
              1,
            );
          }
        }

        // Mark 24h time slots
        if (
          item.startTime === '00:00' &&
          ['23:59', '00:00'].includes(item.endTime)
        ) {
          item.time24h = true;
        }

        // Format times for display using the store's timezone
        // The FormattedTimePipe will use the specified timezone
        item.startTime = this.formatTime(
          item.startTime,
          timeFormat,
          locale,
          storeTimezone,
        );
        item.endTime = this.formatTime(
          item.endTime,
          timeFormat,
          locale,
          storeTimezone,
        );
      });
    }

    return resultArray;
  }

  formatTime(
    time: string,
    format: string,
    locale: string,
    timezone?: string,
  ): string {
    if (timezone) {
      return this.formattedTimePipe.transform(time, format, locale, timezone);
    }
    return this.formattedTimePipe.transform(time, format, locale);
  }

  calculateLogoTheme(event: any) {
    const LOGO_HEIGHT_MARGIN = 10;
    const LOGO_HEIGHT_RATIO = 0.75;
    const BACKGROUND_WIDTH_MARGIN = 200;
    const BORDER_SIZE = 10;
    const BRIGHTNESS_THRESHOLD = 128;
    const RED_WEIGHT = 0.2126;
    const GREEN_WEIGHT = 0.7152;
    const BLUE_WEIGHT = 0.0722;

    const { r, g, b } = this.analyseImage(event.target, BORDER_SIZE);
    const brightness = RED_WEIGHT * r + GREEN_WEIGHT * g + BLUE_WEIGHT * b;
    const isDark = brightness < BRIGHTNESS_THRESHOLD;

    const logoMargin = Math.round(
      (event.target.height + LOGO_HEIGHT_MARGIN) * LOGO_HEIGHT_RATIO,
    );
    const backgroundMargin = event.target.width + BACKGROUND_WIDTH_MARGIN;
    const logoBackgrondColor = `rgba(${r},${g},${b},1)`;

    return { logoMargin, backgroundMargin, logoBackgrondColor, isDark };
  }

  analyseImage(img: any, border: any) {
    const canvas = document.createElement('canvas'); // create a canvas element
    const ctx = canvas.getContext('2d'); // get context
    const w = img.naturalWidth; // get actual width..
    const h = img.naturalHeight;

    canvas.width = w; // set canvas size
    canvas.height = h;

    ctx.drawImage(img, 0, 0); // draw in image

    // do checks:, for example:
    // if (border*2 > canvas.width || border*2 > canvas.height) throw "Image too small!";

    // get borders, avoid overlaps (though it does not really matter in this case):
    const top = ctx.getImageData(0, 0, w, border).data;
    const left = ctx.getImageData(0, border, border, h - border * 2).data;
    const right = ctx.getImageData(
      w - border,
      border,
      border,
      h - border * 2,
    ).data;
    const bottom = ctx.getImageData(0, h - border, w, border).data;

    let r = 0;
    let g = 0;
    let b = 0;
    let cnt = 0;

    // count pixels and add up color components: (see function below)
    countBuffer(top);
    countBuffer(left);
    countBuffer(right);
    countBuffer(bottom);

    // calc average
    r = Math.round(r / cnt);
    g = Math.round(g / cnt);
    b = Math.round(b / cnt);

    return { r, g, b };

    function countBuffer(data) {
      let i = 0;
      const len = data.length;
      while (i < len) {
        r += data[i++]; // add red component etc.
        g += data[i++];
        b += data[i++];
        i++;
        cnt++; // count one pixel
      }
    }
  }

  isGoApp() {
    return environment.mode === APPMODE.Customer;
  }

  isGoTemplateApp() {
    return environment.mode === APPMODE.Template;
  }

  /**
   * Template apps that ship a configured bottom menu bar (story #10267) present
   * account-gated tabs with an in-place login prompt instead of the customer
   * UI's redirect-to-login behaviour, so shared screens need to recognise them.
   */
  isTemplateBottomBarApp(): boolean {
    return isTemplateBottomBarApp();
  }

  isGoAdminApp() {
    return environment.mode === APPMODE.Admin;
  }

  /**
   * The PayDroid GoAdmin APK for PAX terminals, which have no Google Mobile
   * Services. See `isPayDroidBuild` above. Card #9881.
   */
  isPayDroidBuild(): boolean {
    return isPayDroidBuild();
  }

  isGoKioskApp() {
    return environment.mode === APPMODE.Kiosk;
  }

  generateBasePath(
    baseUrl: string,
    storeId: number,
    originType: string,
    originDomain: string,
    storeAlias: string,
  ): string {
    let appPath = baseUrl;

    if (appPath === '') {
      appPath = '/';
    }

    if (originType === ORIGIN_TYPE.APP) {
      appPath = '/public';

      if (
        originDomain === 'com.gonnaorder.go' ||
        originDomain === 'com.gonnaorder.godev'
      ) {
        appPath = `/public/customer/store/${storeAlias}`;
      }

      if (
        originDomain === 'com.gonnaorder.goadmin' ||
        originDomain === 'com.gonnaorder.goadmindev'
      ) {
        appPath = `/manager/stores/${storeId}/capture/${storeAlias}`;
      }
    }

    return appPath;
  }

  redirectToAppOrAppStore(appUrl, appStoreLink: string) {
    const timeout = setTimeout(() => {
      window.location.href = appStoreLink;
    }, 1500);
    window.location = appUrl;

    window.addEventListener('blur', () => clearTimeout(timeout));
  }

  isEmail(userName: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(userName);
  }

  getCookieBarElement(): HTMLElement | null {
    return (
      document.getElementById('cookie-bar') ??
      document.getElementById('sticky-cookie')
    );
  }

  getCookieBarHeight(): number {
    const cookieBarElement = this.getCookieBarElement();
    if (!cookieBarElement) {
      return 0;
    }

    const height = cookieBarElement.offsetHeight;
    if (height <= 0) {
      return 0;
    }

    if (
      cookieBarElement.id === 'cookie-bar' &&
      (this.cookieService.get('cookieEnabled') === 'ACCEPT' ||
        this.cookieService.get('cookieEnabled') === 'REJECT')
    ) {
      return 0;
    }

    return height - 1;
  }

  /**
   * This function takes an input string, removes underscores,
   * and capitalizes the first letter of each word in the string.
   *
   * Example: 'example_string_input' -> 'ExampleStringInput'
   */
  public removeSlashAndCapitalizeString(input) {
    const parts = input?.toLowerCase()?.split('_');

    const transformed = parts?.map((part) => {
      return part.charAt(0).toUpperCase() + part.slice(1);
    });

    return transformed?.join('');
  }
  // Factory function providing default values
  createDefaultEventStatistics(): EventStatistics {
    return {
      bookingCount: 0,
      ticketCount: 0,
      reservationCount: 0,
      paidOnlineRevenue: 0,
      bookingConversionRate: 0,
    };
  }

  isAdminUI(): boolean {
    return !!document.querySelector('app-admin-layout');
  }

  getAdminHeaderHeight(): number {
    return document.getElementById('app-header-admin')?.offsetHeight || 0;
  }

  getAdminBreadcrumbNavigationHeight(): number {
    return document.getElementById('breadcrumb-navigation')?.offsetHeight || 0;
  }

  /**
   * Where the admin bar's bottom edge is painted right now, in viewport
   * coordinates, clamped at 0.
   *
   * getAdminHeaderHeight() answers "how tall is it", which is the same thing
   * only while the bar is pinned at the very top. Anything that positions
   * itself under the bar has to ask where it actually IS: in Order Capture it
   * is pinned at some scroll positions and out of reach at others, and
   * reserving its height while it is gone leaves a hole with the catalog
   * showing through.
   */
  getAdminHeaderBottom(): number {
    const header = document.getElementById('app-header-admin');
    return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  }

  /**
   * Whether `element` can be scrolled programmatically at all.
   *
   * Overflow decides this, size does not. scrollBy()/scrollTo() on a box with
   * no scrolling box does nothing — CSSOM-View aborts silently: no exception,
   * no movement, nothing for the caller to notice — and scrollHeight vs
   * clientHeight cannot detect that case, because whether an overflowing
   * descendant shows up in an overflow:visible box's scrollHeight depends on
   * how the layout above it is built, not on whether the box scrolls.
   *
   * `hidden` counts as scrollable on purpose: it suppresses the scrollbar, not
   * programmatic scrolling. `clip` and `visible` do not.
   */
  isScrollContainer(element: HTMLElement): boolean {
    if (!element) {
      return false;
    }
    const { overflowY } = getComputedStyle(element);
    return overflowY !== 'visible' && overflowY !== 'clip';
  }

  formatDate(dateString: string, locale?: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);

    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    })
      .format(date)
      .replace(',', '');
  }
  computePayloadDiff(
    original: any,
    updated: any,
    atomicKeys: string[] = ['customAddress', 'media', 'eventAttributeLanguage'],
    parentKey: string | null = null,
  ): any {
    const isAtomic = parentKey && atomicKeys.includes(parentKey);
    const isObj = (v: any) => v && typeof v === 'object' && !Array.isArray(v);

    if (!isObj(updated) || !isObj(original) || isAtomic) {
      if (JSON.stringify(original) !== JSON.stringify(updated)) {
        return updated;
      }
      return {};
    }

    const diff: any = {};
    for (const key of Object.keys(updated)) {
      const oVal = original?.[key];
      const uVal = updated[key];

      if (isObj(uVal) && isObj(oVal)) {
        const nested = this.computePayloadDiff(oVal, uVal, atomicKeys, key);
        if (
          isObj(nested) ? Object.keys(nested).length > 0 : nested !== undefined
        ) {
          diff[key] = nested;
        }
      } else {
        if (JSON.stringify(oVal) !== JSON.stringify(uVal)) {
          diff[key] = uVal;
        }
      }
    }
    return diff;
  }

  isObject(obj: any): boolean {
    return obj && typeof obj === 'object' && !Array.isArray(obj);
  }

  rgbToHsl(rgb: string): [number, number, number] {
    const result = rgb.match(/\d+/g);
    if (!result) return [0, 0, 0];

    let [r, g, b] = result.map(Number).map((c) => c / 255);

    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return [h * 360, s, l];
  }

  hslToRgb(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
      g = 0,
      b = 0;

    if (h >= 0 && h < 60) [r, g, b] = [c, x, 0];
    else if (h >= 60 && h < 120) [r, g, b] = [x, c, 0];
    else if (h >= 120 && h < 180) [r, g, b] = [0, c, x];
    else if (h >= 180 && h < 240) [r, g, b] = [0, x, c];
    else if (h >= 240 && h < 300) [r, g, b] = [x, 0, c];
    else if (h >= 300 && h < 360) [r, g, b] = [c, 0, x];

    const r255 = Math.round((r + m) * 255);
    const g255 = Math.round((g + m) * 255);
    const b255 = Math.round((b + m) * 255);

    return `rgb(${r255}, ${g255}, ${b255})`;
  }

  generateGradientColors(baseRgb: string, count: number): string[] {
    const [h, s, l] = this.rgbToHsl(baseRgb);

    const start = l * 0.45;
    const end = l * 0.95;
    const step = (start - end) / Math.max(count - 1, 1);

    const variants: string[] = [];
    for (let i = 0; i < count; i++) {
      const newL = Math.max(0, Math.min(1, start - step * i));
      variants.push(this.hslToRgb(h, s, newL));
    }

    return variants;
  }

  wrapLabel(text: string, maxChars: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';

    for (const w of words) {
      if ((current + (current ? ' ' : '') + w).length > maxChars) {
        if (current) {
          lines.push(current);
        }
        current = w;
      } else {
        current = current ? `${current} ${w}` : w;
      }
    }

    if (current) {
      lines.push(current);
    }
    return lines;
  }

  async generateThumbnail(imageUrl): Promise<string> {
    try {
      const video = document.createElement('video');
      video.src = imageUrl;
      video.crossOrigin = 'anonymous';
      video.currentTime = 2;

      await new Promise<void>((resolve, reject) => {
        video.addEventListener('loadeddata', () => resolve());
        video.addEventListener('error', () =>
          reject('Failed to load the video.'),
        );
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        let image = canvas.toDataURL('image/png');
        video.remove();
        return image;
      }
    } catch (error) {
      console.warn('Fail to generate thumbnail');
    }
  }
  extractFirstName(fullName: string | null): string {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    return parts[0] || '';
  }

  extractLastName(fullName: string | null): string {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : '';
  }

  /**
   * Check if Expected Dropoff should be shown based on delivery status
   * Works for both single order (object) and multiple orders (array)
   * Hides dropoff info once order is DELIVERED
   */
  shouldShowExpectedDropoff(orderOrOrders: any | any[]): boolean {
    const hideAfterStatuses = ['DELIVERED'];

    // Handle array of orders - show column if ANY order should show dropoff
    if (Array.isArray(orderOrOrders)) {
      if (orderOrOrders.length === 0) {
        return true; // Show by default for empty array
      }
      // Show if ANY order doesn't have a delivery status or hasn't been delivered yet
      return orderOrOrders.some(
        (order) =>
          !order.deliveryStatus ||
          !hideAfterStatuses.includes(order.deliveryStatus),
      );
    }

    // Handle single order - check delivery status
    const order = orderOrOrders;
    if (!order || !order.deliveryStatus) {
      return true; // Show by default if no delivery status
    }

    return !hideAfterStatuses.includes(order.deliveryStatus);
  }

  /**
   * Check if Expected Pickup should be shown based on delivery status
   * Works for both single order (object) and multiple orders (array)
   * Hides pickup info once order is PICKED_UP or beyond
   */
  shouldShowExpectedPickup(orderOrOrders: any | any[]): boolean {
    const hideAfterStatuses = [
      'PICKED_UP',
      'DROPOFF_EN_ROUTE',
      'DROPOFF_ARRIVED',
      'DELIVERED',
    ];

    // Handle array of orders - show column if ANY order should show pickup
    if (Array.isArray(orderOrOrders)) {
      if (orderOrOrders.length === 0) {
        return true; // Show by default for empty array
      }
      // Show if ANY order doesn't have a delivery status or hasn't been picked up yet
      return orderOrOrders.some(
        (order) =>
          !order.deliveryStatus ||
          !hideAfterStatuses.includes(order.deliveryStatus),
      );
    }

    // Handle single order - check delivery status
    const order = orderOrOrders;
    if (!order || !order.deliveryStatus) {
      return true; // Show by default if no delivery status
    }

    return !hideAfterStatuses.includes(order.deliveryStatus);
  }

  /**
   * The Thank-You pages should keep polling order status while either the
   * merchant lifecycle (SUBMITTED/RECEIVED/DRAFT) or the courier lifecycle
   * (isBeingDelivered, before a terminal delivery state) is still active.
   */
  shouldKeepThankYouPolling(orderData: any): boolean {
    if (!orderData) {
      return false;
    }
    const merchantActive =
      orderData.status === 'SUBMITTED' ||
      orderData.status === 'RECEIVED' ||
      orderData.status === 'DRAFT';
    const courierActive =
      orderData.isBeingDelivered === true &&
      orderData.deliveryStatus !== 'DELIVERED' &&
      orderData.deliveryStatus !== 'CANCELLED' &&
      orderData.deliveryStatus !== 'FAILED';
    return merchantActive || courierActive;
  }

  /**
   * Check if GPS coordinates are valid for showing the map icon
   * Returns true if both latitude and longitude are valid numbers and not (0,0)
   */
  shouldShowMapIcon(order: any): boolean {
    const lat = order?.latitude;
    const lng = order?.longitude;

    // Check if coordinates exist and are not null/undefined
    if (lat == null || lng == null) {
      return false;
    }

    // Convert to numbers and check if they are valid numbers
    const latNum = Number(lat);
    const lngNum = Number(lng);

    // Check if conversion resulted in valid numbers
    if (isNaN(latNum) || isNaN(lngNum)) {
      return false;
    }

    // Check for the default (0,0) coordinates which should be treated as invalid
    if (latNum === 0 && lngNum === 0) {
      return false;
    }

    return true;
  }

  /**
   * Debounces input changes and only emits when there is no active autocomplete instance.
   * Accepts either an autocomplete instance or a zero-arg getter returning the current instance.
   */
  debounceAutoComplete(
    control: AbstractControl,
    unsubscribe$: Subject<void>,
    autocompleteElementOrGetter:
      | google.maps.places.Autocomplete
      | (() => google.maps.places.Autocomplete),
  ): Observable<any> {
    const getAutocomplete = () => {
      if (typeof autocompleteElementOrGetter === 'function') {
        try {
          return (autocompleteElementOrGetter as any)();
        } catch {
          return null;
        }
      }
      return autocompleteElementOrGetter as any;
    };

    return control.valueChanges.pipe(
      // takeUntil sits AFTER debounceTime on purpose. The other way round,
      // notifying unsubscribe$ COMPLETES debounceTime's source, and RxJS
      // debounceTime emits its pending buffered value on completion rather than
      // dropping it. Every caller passes a destroy/teardown subject here and
      // attaches a Google Places widget in its subscribe body, so that final
      // emission attached a throwaway Autocomplete (and opened a Places
      // session) while the component was being torn down. With takeUntil last,
      // the notification unsubscribes the chain instead of completing it and
      // the pending value is discarded. Normal emissions are unaffected.
      debounceTime(this.isMobileApp() ? 400 : 300),
      takeUntil(unsubscribe$),
      distinctUntilChanged(),
      filter((value) => !!value && value.length > 2),
      filter(() => !getAutocomplete()),
    );
  }
}
